import * as React12 from 'react';
import { Fragment as Fragment$1 } from 'react';
import { DataTableFacetedFilter, ChainAvatar, Badge, FinalityPill, RiskBadge, cn, selectionColumn, useDataTable, ErrorState, DataTableToolbar, LiveModeControl, Skeleton, DataTable, EmptyState, DataTablePagination, DrilldownPanel, useNow, useQuery, notifyError, buildScopeChoices as buildScopeChoices$1, ScopeSwitcher, Spinner, TagList, MiddleEllipsis, CopyButton, SagaPill, TokenValue, AddressDisplay, HashDisplay, AttributionChips } from '@authe/ui-core';
import { fieldLabels, TX_FIELDS, fieldLabel, getHashTruncation, getAddressTruncation, TABLE_DENSITY_ROW_HEIGHT } from '@authe/fe-commons/display';
import { FinalityState, getChainMetadata, coerceFinality, resolveFinality, confirmationProgress, chainDisplayName } from '@authe/fe-commons/chains';
export { coerceFinality } from '@authe/fe-commons/chains';
import { sagaIdLabel, titleRole, sagaLabel } from '@authe/fe-commons/grouping';
import { formatRelativeTime, formatTimestamp } from '@authe/fe-commons/time';
import { isGlobalTenantSegment } from '@authe/fe-commons/tenancy';
import { riskLevelFromScore } from '@authe/fe-commons/precedence';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { readLocalJsonOptional, writeLocalJson, parseGroupView, cdnUrlForKey, isOffloadPlaceholder, rehydrateItem, createEnvelopeBodyLoader, mergeSlice } from '@authe/fe-commons/storage';
import { formatUsd } from '@authe/fe-commons/value';
import { useSavedViews } from '@authe/saved-views';
import { ExportMenu, DEFAULT_TX_COLUMNS } from '@authe/export';
export { DEFAULT_TX_COLUMNS, exportTransactionsCsv } from '@authe/export';
import { useOptionalStreamingClient, useChainTips } from '@authe/streaming';
import { createLogger } from '@authe/fe-commons/logging';
import { scopeReadQuery, TransactionDetailSchema, transactionScopeDescription, transactionScopeLabel } from '@authe/fe-commons/models';
import { toNetFlowRows, toTokenFlowRows, coerceTransactionRows, coerceTransactionRow } from '@authe/fe-commons/transpose';
export { coerceTransactionRow, coerceTransactionRows, toNetFlowRows, toTokenFlowRows, txEntryUniqueAddresses, unwrapTxEnvelope } from '@authe/fe-commons/transpose';
import { useTransport, useGraphqlQuery } from '@authe/ui-core/api';
import { unwrapData } from '@authe/fe-commons/api';
import { fetchLatestTransactions } from '@authe/fe-commons/graphql';

// src/TransactionTable.tsx
function isSystemTxRow(row) {
  return isGlobalTenantSegment(row.org_id) && isGlobalTenantSegment(row.tenant_id);
}
function rowProtocols(row) {
  const primary = typeof row.primary_protocol_id === "string" ? row.primary_protocol_id : void 0;
  const rest = Array.isArray(row.protocol_ids) ? row.protocol_ids.filter((id) => Boolean(id)) : [];
  return [...new Set(primary ? [primary, ...rest] : rest)];
}
function getRowEnrichment(row) {
  const candidate = row.enrichment;
  return candidate && typeof candidate === "object" ? candidate : void 0;
}
function resolveRisk(e) {
  const c = e?.counterparty;
  if (!c) return null;
  if (c.riskLevel) return c.riskLevel;
  if (typeof c.riskScore === "number") return riskLevelFromScore(c.riskScore) ?? null;
  return null;
}
var inSelectedSet = (row, columnId, filterValue) => {
  const selected = filterValue;
  if (!selected || selected.length === 0) return true;
  return selected.includes(String(row.getValue(columnId)));
};
var intersectsSelectedSet = (row, columnId, filterValue) => {
  const selected = filterValue;
  if (!selected || selected.length === 0) return true;
  const values = row.getValue(columnId);
  if (!Array.isArray(values)) return selected.includes(String(values));
  return values.some((value) => selected.includes(String(value)));
};
function normalizeHashSearch(value) {
  return String(value ?? "").trim().toLowerCase().replace(/^0x/, "");
}
var hashContains = (row, columnId, filterValue) => {
  const raw = String(filterValue ?? "").trim().toLowerCase();
  if (!raw) return true;
  const hay = normalizeHashSearch(row.getValue(columnId));
  const parts = raw.split(/…|\.\.\./);
  if (parts.length === 2) {
    const head = parts[0].replace(/^0x/, "");
    const tail = parts[1];
    return hay.startsWith(head) && (tail === "" || hay.endsWith(tail));
  }
  return hay.includes(normalizeHashSearch(raw));
};
hashContains.autoRemove = (value) => !normalizeHashSearch(value);
function idMatches(id, needle) {
  const hay = id.toLowerCase();
  const parts = needle.split(/…|\.\.\./);
  if (parts.length === 2) {
    const [head, tail] = parts;
    return hay.startsWith(head) && (tail === "" || hay.endsWith(tail));
  }
  return hay.includes(needle);
}
var idsContain = (row, columnId, filterValue) => {
  const needle = String(filterValue ?? "").trim().toLowerCase();
  if (!needle) return true;
  const raw = row.getValue(columnId);
  const ids = Array.isArray(raw) ? raw : [raw];
  return ids.some((id) => idMatches(String(id ?? ""), needle));
};
idsContain.autoRemove = (value) => !String(value ?? "").trim();
var sortByBigIntString = (a, b, columnId) => {
  const av = safeBigInt(a.getValue(columnId));
  const bv = safeBigInt(b.getValue(columnId));
  return av < bv ? -1 : av > bv ? 1 : 0;
};
function safeBigInt(value) {
  try {
    return BigInt(value || "0");
  } catch {
    return 0n;
  }
}
function monoColumnWidth(head, tail, affordancePx = 72) {
  return Math.round((head + tail + 1) * 8.5) + affordancePx;
}
var EmDash = () => /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" });
function why(ref) {
  return { confidence: ref.confidence, explanation: ref.explanation, lifecycle: ref.lifecycle };
}
function labelChips(refs) {
  return (refs ?? []).map((l, i) => ({
    id: `${l.label}-${i}`,
    name: l.label,
    value: l.value,
    scope: l.scope,
    logo: l.logo,
    color: l.color,
    ...why(l)
  }));
}
function tagChips(refs) {
  return (refs ?? []).map((t, i) => ({
    id: `${t.label}-${i}`,
    name: t.label,
    severity: t.severity,
    logo: t.logo,
    color: t.color,
    ...why(t)
  }));
}
function groupChips(refs) {
  return (refs ?? []).map((g) => ({
    id: g.id,
    name: g.name ?? g.id,
    count: g.count,
    ...why(g)
  }));
}
function entityChips(entity) {
  if (!entity) return [];
  return [
    {
      id: entity.id,
      name: entity.name,
      entityType: entity.entityType,
      logo: entity.logo,
      color: entity.color,
      isPrimary: entity.isPrimary,
      ...why(entity)
    }
  ];
}
function counterpartyBundle(e) {
  const c = e?.counterparty;
  return {
    entities: entityChips(c?.entity),
    labels: labelChips(c?.labels),
    tags: tagChips(c?.tags),
    groups: groupChips(c?.groups)
  };
}
function bundleIsEmpty(bundle) {
  return ["entities", "labels", "tags", "groups"].every(
    (k) => (bundle[k] ?? []).length === 0
  );
}
function createTransactionColumns(opts = {}) {
  const {
    explorerLinks = true,
    logoResolver,
    locale,
    onDrilldown,
    columns,
    chainHeads,
    getSaga,
    renderProtocol,
    chipPrefs,
    counterpartyChips = false
  } = opts;
  const getEnrichment = opts.getEnrichment ?? getRowEnrichment;
  const hasEnrichmentReader = Boolean(opts.getEnrichment);
  const hashTrunc = getHashTruncation();
  const addrTrunc = getAddressTruncation();
  const searchFilter = !getSaga ? hashContains : (row, columnId, filterValue, addMeta) => {
    if (hashContains(row, columnId, filterValue)) return true;
    const needle = String(filterValue ?? "").trim().toLowerCase();
    return (getSaga(row.original)?.ids ?? []).some((id) => idMatches(id.value, needle));
  };
  searchFilter.autoRemove = hashContains.autoRemove;
  const chipColumn = (id, kind, read, max) => ({
    id,
    header: fieldLabel(id),
    enableSorting: false,
    cell: ({ row }) => /* @__PURE__ */ jsx(
      AttributionChips,
      {
        bundle: read(getEnrichment(row.original)),
        kinds: [kind],
        max,
        popoverTitle: fieldLabel(id),
        userPrefs: chipPrefs,
        empty: /* @__PURE__ */ jsx(EmDash, {})
      }
    )
  });
  const CounterpartyChips = ({
    enrichment
  }) => {
    if (!counterpartyChips) return null;
    const bundle = counterpartyBundle(enrichment);
    if (bundleIsEmpty(bundle)) return null;
    return /* @__PURE__ */ jsx(AttributionChips, { bundle, popoverTitle: "Counterparty", userPrefs: chipPrefs });
  };
  const labelColumn = (id, read) => chipColumn(id, "label", (e) => ({ labels: labelChips(read(e)) }));
  const tagColumn = (id, read) => chipColumn(id, "tag", (e) => ({ tags: tagChips(read(e)) }));
  const groupColumn = (id, read) => chipColumn(id, "group", (e) => ({ groups: groupChips(read(e)) }), 2);
  const entityColumn = (id, read) => chipColumn(id, "entity", (e) => {
    const entity = read(e);
    return entity ? {
      entities: entityChips(entity)
    } : {};
  });
  const registry = {
    select: { id: "select" },
    // placeholder; real selection column injected by TransactionTable
    chain_id: {
      accessorKey: "chain_id",
      header: fieldLabel(TX_FIELDS.chainId),
      filterFn: inSelectedSet,
      enableSorting: false,
      cell: ({ getValue }) => /* @__PURE__ */ jsx(ChainAvatar, { chain: String(getValue() ?? ""), size: "sm", logoResolver })
    },
    tx_hash: {
      accessorKey: "tx_hash",
      header: fieldLabel(TX_FIELDS.txHash),
      enableSorting: false,
      // The only pinned column. Every other one is a matter of what this table is
      // for, but the hash is the row's identity: hide it and a row can't be
      // copied, quoted in a report, drilled into, or told apart from the next one
      // — the table stops being about transactions. Everything else stays the
      // user's call.
      enableHiding: false,
      // Tolerant substring match against the FULL hash accessor — the cell is
      // rendered middle-truncated (cosmetic) but search hits the complete value,
      // normalising whitespace/case/`0x` so a paste of the whole hash (or any
      // fragment of it) matches. Widened to the row's cross-chain ids where the
      // host has them; see `searchFilter`.
      filterFn: searchFilter,
      size: monoColumnWidth(hashTrunc.head, hashTrunc.tail),
      cell: ({ row }) => {
        const hash = row.original.tx_hash;
        if (!hash) return /* @__PURE__ */ jsx(EmDash, {});
        const display = /* @__PURE__ */ jsx(
          HashDisplay,
          {
            hash,
            chain: row.original.chain_id,
            truncate: hashTrunc,
            canonicalTxId: row.original.canonical_tx_id ?? void 0,
            showExplorerIcon: explorerLinks && !onDrilldown,
            onDrilldown
          }
        );
        if (!isSystemTxRow(row.original)) return display;
        return /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
          display,
          /* @__PURE__ */ jsx(
            Badge,
            {
              variant: "outline",
              className: "shrink-0 px-1 py-0 text-[10px] font-normal text-muted-foreground",
              title: "Processed by an always-on system pipeline, visible to every tenant",
              children: "System"
            }
          )
        ] });
      }
    },
    block_timestamp: {
      accessorKey: "block_timestamp",
      header: fieldLabel(TX_FIELDS.blockTimestamp),
      cell: ({ getValue }) => {
        const ts = Number(getValue() ?? 0);
        if (!ts) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx("span", { title: formatTimestamp(ts, { locale }), className: "whitespace-nowrap text-muted-foreground", children: formatRelativeTime(ts, { locale }) });
      }
    },
    from: {
      id: "from",
      header: fieldLabel(TX_FIELDS.from),
      enableSorting: false,
      size: monoColumnWidth(addrTrunc.head, addrTrunc.tail),
      cell: ({ row }) => {
        const addr = getEnrichment(row.original)?.from;
        if (!addr) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx(AddressDisplay, { address: addr, chain: row.original.chain_id, onDrilldown, truncate: addrTrunc });
      }
    },
    to: {
      id: "to",
      header: fieldLabel(TX_FIELDS.to),
      enableSorting: false,
      size: monoColumnWidth(addrTrunc.head, addrTrunc.tail),
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        const addr = e?.to;
        if (!addr) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsxs("span", { className: "flex flex-col items-start gap-1", children: [
          /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address: addr,
              chain: row.original.chain_id,
              entityLabel: e?.counterparty?.entity?.name,
              onDrilldown,
              truncate: addrTrunc
            }
          ),
          /* @__PURE__ */ jsx(CounterpartyChips, { enrichment: e })
        ] });
      }
    },
    value: {
      id: "value",
      accessorFn: (row) => row.total_value_raw ?? "0",
      header: fieldLabel(TX_FIELDS.totalValueRaw),
      sortingFn: sortByBigIntString,
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        return /* @__PURE__ */ jsx(
          TokenValue,
          {
            amount: row.original.total_value_raw ?? "0",
            chain: row.original.chain_id,
            valueUsd: e?.valueUsd ?? null,
            direction: e?.direction ?? null
          }
        );
      }
    },
    finality: {
      id: "finality",
      // Read straight off the row, so the pill, the sort order and the faceted
      // counts are one answer rather than three. Promoting the persisted state
      // with the live head is `useLiveFinalityRows`' job, on the DATA — an
      // accessor that did it here would be computed once per row and cached, and
      // the facet panel would go on offering "Observed" for rows the cells had
      // already promoted (see that hook's note on TanStack's value cache).
      accessorFn: (row) => coerceFinality(row.finality) ?? FinalityState.UNKNOWN,
      header: fieldLabel(TX_FIELDS.finality),
      filterFn: inSelectedSet,
      cell: ({ row, getValue }) => {
        const state = getValue();
        const chainId = String(row.original.chain_id ?? "").toLowerCase();
        const confirmations = confirmationProgress(state, {
          blockNumber: row.original.block_number ?? null,
          head: chainHeads?.[chainId] ?? null,
          chainOrId: chainId
        });
        return /* @__PURE__ */ jsx(FinalityPill, { state, confirmations: confirmations ?? void 0 });
      }
    },
    type: {
      accessorKey: "type",
      header: fieldLabel(TX_FIELDS.type),
      filterFn: inSelectedSet,
      cell: ({ row, getValue }) => {
        const isContract = getEnrichment(row.original)?.contractInteraction;
        const label = String(getValue() ?? "transaction");
        return isContract ? /* @__PURE__ */ jsx(
          Badge,
          {
            variant: "outline",
            className: "border-risk-medium/40 bg-risk-medium/10 font-normal capitalize text-risk-medium",
            title: "Contract interaction",
            children: label
          }
        ) : /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "font-normal capitalize", children: label });
      }
    },
    // --- the transaction's own attribution -----------------------------------
    // What the table's bulk bar and the inspector write via `PATCH
    // /transactions/{id}`. Tenant-wide and chain-agnostic: a transaction's
    // overlays carry no per-user visibility, so these chips have no scope dot.
    tx_labels: labelColumn("tx_labels", (e) => e?.labels),
    tx_tags: tagColumn("tx_tags", (e) => e?.tags),
    tx_groups: groupColumn("tx_groups", (e) => e?.groups),
    tx_entity: entityColumn("tx_entity", (e) => e?.entity),
    // --- the counterparty address's attribution ------------------------------
    // A DIFFERENT claim about a DIFFERENT thing: written per `(chain, address)`
    // through the assignments API, by someone saying "this wallet is Binance",
    // not "this transfer is suspicious". Describes the row's `to` end (or `from`
    // on a one-ended row) — see the host's enrichment reader for which.
    counterparty_labels: labelColumn("counterparty_labels", (e) => e?.counterparty?.labels),
    counterparty_tags: tagColumn("counterparty_tags", (e) => e?.counterparty?.tags),
    counterparty_groups: groupColumn("counterparty_groups", (e) => e?.counterparty?.groups),
    counterparty_entity: entityColumn("counterparty_entity", (e) => e?.counterparty?.entity),
    counterparty_risk: {
      id: "counterparty_risk",
      header: fieldLabel("counterparty_risk"),
      enableSorting: false,
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        const level = resolveRisk(e);
        return level ? /* @__PURE__ */ jsx(RiskBadge, { level, score: e?.counterparty?.riskScore }) : /* @__PURE__ */ jsx(EmDash, {});
      }
    },
    // Distinct from `groups` above, which shows the CURATED catalog collections a
    // row was filed into. This is the machine cross-chain saga: the legs a
    // protocol's own correlation id ties together, and whether they all arrived.
    saga: {
      id: "saga",
      header: fieldLabel("saga"),
      enableSorting: false,
      cell: ({ row }) => {
        const saga = getSaga?.(row.original);
        if (!saga || saga.total === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx(
            SagaPill,
            {
              label: sagaLabel(saga.sagaKind),
              missing: saga.missingRoles.map(titleRole),
              complete: saga.complete,
              total: saga.total,
              confirmed: saga.confirmed ?? false
            }
          ),
          saga.role ? /* @__PURE__ */ jsx("span", { className: "text-[10px] capitalize text-muted-foreground", children: saga.role }) : null
        ] });
      }
    },
    // The correlation id itself, where `saga` above renders whether every leg
    // arrived. This is the value that goes into a report, or into the protocol's
    // own explorer, and it's what makes two rows on different chains provably the
    // same message. Both columns read the same derived sagas, so a host already
    // showing one pays nothing to show the other.
    cross_chain_id: {
      id: "cross_chain_id",
      header: fieldLabel("cross_chain_id"),
      enableSorting: false,
      // The COMPLETE ids: the cell is middle-truncated for width, but a pasted
      // message id has to find its row.
      accessorFn: (row) => (getSaga?.(row)?.ids ?? []).map((id) => id.value),
      filterFn: idsContain,
      size: monoColumnWidth(hashTrunc.head, hashTrunc.tail),
      cell: ({ row }) => {
        const saga = getSaga?.(row.original);
        const ids = saga?.ids ?? [];
        if (ids.length === 0) {
          return saga?.pending ? /* @__PURE__ */ jsx("span", { className: "text-xs italic text-muted-foreground", title: "This row's decoded body hasn't been loaded, so its cross-chain ids are unknown.", children: "Not decoded" }) : /* @__PURE__ */ jsx(EmDash, {});
        }
        return /* @__PURE__ */ jsx(
          TagList,
          {
            popoverTitle: "Cross-chain ids",
            max: 1,
            items: ids.map((id) => /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx(
                MiddleEllipsis,
                {
                  value: id.value,
                  head: hashTrunc.head,
                  tail: hashTrunc.tail,
                  title: `${sagaIdLabel(id.label)}: ${id.value}`,
                  className: "text-xs"
                }
              ),
              /* @__PURE__ */ jsx(CopyButton, { value: id.value, label: `Copy ${sagaIdLabel(id.label)}` })
            ] }, id.value))
          }
        );
      }
    },
    primary_entity_id: {
      accessorKey: "primary_entity_id",
      header: fieldLabel(TX_FIELDS.primaryEntityId),
      enableSorting: false,
      cell: ({ getValue }) => {
        const id = getValue();
        return id ? /* @__PURE__ */ jsx("span", { className: "text-sm", children: id }) : /* @__PURE__ */ jsx(EmDash, {});
      }
    },
    protocol: {
      id: "protocol",
      // Faceting counts each protocol separately rather than treating a row's
      // whole set as one value, so the toolbar filter can offer "chainlink_ccip
      // (12)" over rows that also carry three other protocols.
      accessorFn: (row) => rowProtocols(row),
      getUniqueValues: (row) => rowProtocols(row),
      header: fieldLabel("protocol"),
      enableSorting: false,
      filterFn: intersectsSelectedSet,
      cell: ({ row }) => {
        const protocols = rowProtocols(row.original);
        if (protocols.length === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx(
          TagList,
          {
            popoverTitle: "Protocols",
            max: 2,
            items: protocols.map((id) => (
              // The neutral fallback shows the raw id rather than a humanized
              // one: `erc20` / `chainlink_ccip` are what the API returns and what
              // an investigator cross-references, and Title Case would render
              // them as "Erc20". Hosts that want display names inject a renderer
              // backed by the brand registry, which actually knows them.
              /* @__PURE__ */ jsx(Fragment$1, { children: renderProtocol?.(id, row.original) ?? /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "font-normal", children: id }) }, id)
            ))
          }
        );
      }
    }
  };
  const defaultColumns = hasEnrichmentReader ? [
    "chain_id",
    "tx_hash",
    "from",
    "to",
    "value",
    "counterparty_risk",
    "counterparty_labels",
    "counterparty_tags",
    "type",
    "finality"
  ] : ["chain_id", "tx_hash", "block_timestamp", "value", "finality", "type", "primary_entity_id"];
  const selected = columns ?? defaultColumns;
  return selected.filter((id) => id !== "select" && registry[id]).map((id) => registry[id]);
}
var TX_COLUMN_LABELS = fieldLabels(
  [
    TX_FIELDS.chainId,
    TX_FIELDS.txHash,
    TX_FIELDS.blockTimestamp,
    TX_FIELDS.from,
    TX_FIELDS.to,
    TX_FIELDS.finality,
    TX_FIELDS.type,
    TX_FIELDS.primaryEntityId
  ],
  {
    value: fieldLabel(TX_FIELDS.totalValueRaw),
    saga: fieldLabel("saga"),
    cross_chain_id: fieldLabel("cross_chain_id"),
    protocol: fieldLabel("protocol"),
    // Both attribution subjects, spelled out: this map IS the column menu, and
    // the menu is the only place a user learns that the table can tell them apart.
    tx_labels: fieldLabel("tx_labels"),
    tx_tags: fieldLabel("tx_tags"),
    tx_groups: fieldLabel("tx_groups"),
    tx_entity: fieldLabel("tx_entity"),
    counterparty_labels: fieldLabel("counterparty_labels"),
    counterparty_tags: fieldLabel("counterparty_tags"),
    counterparty_groups: fieldLabel("counterparty_groups"),
    counterparty_entity: fieldLabel("counterparty_entity"),
    counterparty_risk: fieldLabel("counterparty_risk")
  }
);
var PREFIX = "authe:tx-table:cols:";
function readInitialColumnVisibility(viewKey, fallback = {}) {
  if (!viewKey) return fallback;
  const stored = readLocalJsonOptional(`${PREFIX}${viewKey}`);
  return stored ? { ...fallback, ...stored } : fallback;
}
function withPinnedColumnsVisible(visibility, columns) {
  const pinned = columns.filter((c) => c.enableHiding === false).map((c) => columnKey(c));
  if (!pinned.some((id) => id !== void 0 && visibility[id] === false)) return visibility;
  const repaired = { ...visibility };
  for (const id of pinned) if (id !== void 0) delete repaired[id];
  return repaired;
}
function columnKey(column) {
  if (column.id) return column.id;
  const key = column.accessorKey;
  return typeof key === "string" ? key : void 0;
}
function usePersistColumnVisibility(table, viewKey) {
  const visibility = table.getState().columnVisibility;
  const serialized = JSON.stringify(visibility);
  const initial = React12.useRef(serialized);
  const changed = React12.useRef(false);
  React12.useEffect(() => {
    if (!viewKey) return;
    if (!changed.current) {
      if (serialized === initial.current) return;
      changed.current = true;
    }
    writeLocalJson(`${PREFIX}${viewKey}`, visibility);
  }, [viewKey, serialized]);
}
var DEFAULT_FACETS = ["chain_id", "finality", "type"];
function humanize(value) {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function defaultLabel(field) {
  return field === "chain_id" ? chainDisplayName : humanize;
}
function deriveOptions(column, override, label = humanize) {
  if (override) return override;
  const facets = column?.getFacetedUniqueValues();
  if (!facets) return [];
  return [...facets.keys()].filter((v) => typeof v === "string" && v !== "").sort().map((value) => ({ value, label: label(value) }));
}
function TransactionFacets({
  table,
  facets = DEFAULT_FACETS,
  options,
  labels
}) {
  return /* @__PURE__ */ jsx(Fragment, { children: facets.map((field) => {
    const column = table.getColumn(field);
    if (!column) return null;
    const opts = deriveOptions(column, options?.[field], labels?.[field] ?? defaultLabel(field));
    if (opts.length === 0) return null;
    return /* @__PURE__ */ jsx(
      DataTableFacetedFilter,
      {
        column,
        title: fieldLabel(field),
        options: opts
      },
      field
    );
  }) });
}

// src/summary.ts
function tally(map) {
  return [...map.entries()].map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}
function summarizeTransactions(rows, getEnrichment = getRowEnrichment) {
  const list = Array.isArray(rows) ? rows : [];
  const chains = /* @__PURE__ */ new Map();
  const finality = /* @__PURE__ */ new Map();
  const types = /* @__PURE__ */ new Map();
  const risk = /* @__PURE__ */ new Map();
  let totalValueUsd = null;
  let contractInteractions = 0;
  const bump = (map, key) => {
    if (key == null || key === "") return;
    map.set(key, (map.get(key) ?? 0) + 1);
  };
  for (const row of list) {
    bump(chains, row.chain_id);
    bump(finality, row.finality ? String(row.finality) : null);
    bump(types, row.type ? String(row.type) : null);
    const e = getEnrichment(row);
    const level = resolveRisk(e);
    if (level) risk.set(level, (risk.get(level) ?? 0) + 1);
    if (e?.valueUsd != null) totalValueUsd = (totalValueUsd ?? 0) + e.valueUsd;
    if (e?.contractInteraction) contractInteractions += 1;
  }
  return {
    total: list.length,
    chains: tally(chains),
    finality: tally(finality),
    types: tally(types),
    risk: [...risk.entries()].map(([level, count]) => ({ level, count })),
    totalValueUsd,
    contractInteractions
  };
}
function Stat({ label, children }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: label }),
    children
  ] });
}
function TransactionLegend({
  rows,
  getEnrichment,
  maxChains = 6,
  className
}) {
  const summary = React12.useMemo(() => summarizeTransactions(rows, getEnrichment), [rows, getEnrichment]);
  const shownChains = summary.chains.slice(0, maxChains);
  const extraChains = summary.chains.length - shownChains.length;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "scroll-fade-x flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border bg-card/40 px-3 py-2 text-sm",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(Stat, { label: "Transactions", children: /* @__PURE__ */ jsx("span", { className: "font-semibold tabular-nums", children: summary.total.toLocaleString() }) }),
        summary.totalValueUsd != null && /* @__PURE__ */ jsx(Stat, { label: "Total", children: /* @__PURE__ */ jsx("span", { className: "font-semibold tabular-nums", children: formatUsd(summary.totalValueUsd) }) }),
        shownChains.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("chain_id"), children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
          shownChains.map((c) => /* @__PURE__ */ jsxs(
            "span",
            {
              className: "inline-flex items-center gap-1",
              title: getChainMetadata(c.key)?.name ?? c.key,
              children: [
                /* @__PURE__ */ jsx(ChainAvatar, { chain: c.key, size: "sm", showName: false }),
                /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: c.count })
              ]
            },
            c.key
          )),
          extraChains > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-muted-foreground", children: [
            "+",
            extraChains
          ] })
        ] }) }),
        summary.finality.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("finality"), children: /* @__PURE__ */ jsx("span", { className: "flex flex-wrap items-center gap-1", children: summary.finality.map((f) => {
          const state = coerceFinality(f.key);
          return state ? /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
            /* @__PURE__ */ jsx(FinalityPill, { state }),
            /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: f.count })
          ] }, f.key) : /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-muted-foreground", children: [
            f.key,
            " ",
            f.count
          ] }, f.key);
        }) }) }),
        summary.risk.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("risk_score"), children: /* @__PURE__ */ jsx("span", { className: "flex flex-wrap items-center gap-1", children: summary.risk.map((r) => /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
          /* @__PURE__ */ jsx(RiskBadge, { level: r.level }),
          /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: r.count })
        ] }, r.level)) }) }),
        summary.contractInteractions > 0 && /* @__PURE__ */ jsx(Stat, { label: "Contracts", children: /* @__PURE__ */ jsx("span", { className: "font-medium tabular-nums", children: summary.contractInteractions }) })
      ]
    }
  );
}
var CLIENT_PAGE_SIZE = 1e5;
function txRowId(row) {
  return row.canonical_tx_id ?? row.tx_hash ?? void 0;
}
function txSelectionRowId(row, index) {
  return txRowId(row) ?? `idx:${index}`;
}
var TX_TABLE_OPTIONS = { getRowId: txSelectionRowId };
function TransactionTable({
  rows,
  columns,
  isLoading = false,
  isFetching = false,
  error,
  onRetry,
  onRowClick,
  activeRowId,
  enableSelection = false,
  enableToolbar = true,
  searchPlaceholder = "Search by tx hash\u2026",
  facetedFilters,
  facets,
  toolbarActions,
  selectionActions,
  pagination,
  viewKey,
  defaultColumnVisibility,
  columnVisibility,
  onColumnVisibilityChange,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  onSortingChange,
  density,
  rowHeight = 44,
  rowClassName,
  live,
  liveMode,
  onLiveModeChange,
  liveAvailable = true,
  showLegend = false,
  getEnrichment = getRowEnrichment,
  renderRowDetail,
  detailTitle,
  detailEyebrow = "Transaction",
  detailSubtitle,
  detailFooter,
  height = "h-[600px]",
  emptyTitle = "No transactions",
  emptyDescription = "No transactions match the current filters.",
  className
}) {
  const resolvedColumns = React12.useMemo(() => {
    const base = columns ?? createTransactionColumns();
    return enableSelection ? [selectionColumn(), ...base] : base;
  }, [columns, enableSelection]);
  const initialColumnVisibility = React12.useMemo(
    () => withPinnedColumnsVisible(
      readInitialColumnVisibility(viewKey, defaultColumnVisibility),
      resolvedColumns
    ),
    // The surface default only seeds the FIRST render (`useDataTable` holds it as
    // initial state), so re-reading it when the object identity changes would be
    // misleading — it would look reactive without being so.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewKey]
  );
  const resolvedVisibility = React12.useMemo(
    () => columnVisibility ? withPinnedColumnsVisible(columnVisibility, resolvedColumns) : void 0,
    [columnVisibility, resolvedColumns]
  );
  const table = useDataTable({
    data: rows,
    columns: resolvedColumns,
    pageSize: CLIENT_PAGE_SIZE,
    enableRowSelection: enableSelection,
    initialColumnVisibility,
    columnVisibility: resolvedVisibility,
    onColumnVisibilityChange,
    columnFilters,
    onColumnFiltersChange,
    sorting,
    onSortingChange,
    tableOptions: TX_TABLE_OPTIONS
  });
  usePersistColumnVisibility(table, viewKey);
  const [detailRow, setDetailRow] = React12.useState(null);
  const handleRowClick = React12.useCallback(
    (row) => {
      onRowClick?.(row);
      if (renderRowDetail) setDetailRow(row);
    },
    [onRowClick, renderRowDetail]
  );
  const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
  const clearSelection = React12.useCallback(() => table.resetRowSelection(), [table]);
  const filteredRows = table.getFilteredRowModel().rows.map((row) => row.original);
  const effectiveRowHeight = density ? TABLE_DENSITY_ROW_HEIGHT[density] : rowHeight;
  const rowClickHandler = onRowClick || renderRowDetail ? handleRowClick : void 0;
  if (error) {
    return /* @__PURE__ */ jsx(ErrorState, { error, onRetry, className });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    enableToolbar && /* @__PURE__ */ jsxs(
      DataTableToolbar,
      {
        table,
        searchColumn: "tx_hash",
        searchPlaceholder,
        viewOptionLabels: TX_COLUMN_LABELS,
        children: [
          onLiveModeChange && /* @__PURE__ */ jsx(
            LiveModeControl,
            {
              mode: liveMode ?? "off",
              onChange: onLiveModeChange,
              liveAvailable,
              size: "sm"
            }
          ),
          live,
          facets && /* @__PURE__ */ jsx(TransactionFacets, { table, ...facets }),
          facetedFilters,
          toolbarActions
        ]
      }
    ),
    showLegend && filteredRows.length > 0 && /* @__PURE__ */ jsx(TransactionLegend, { rows: filteredRows, getEnrichment }),
    enableSelection && selectionActions && selectedRows.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm", children: [
      /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
        selectedRows.length,
        " selected"
      ] }),
      /* @__PURE__ */ jsx("div", { className: "ml-auto flex items-center gap-2", children: selectionActions(selectedRows, { clearSelection }) })
    ] }),
    isLoading && rows.length === 0 ? /* @__PURE__ */ jsx("div", { className: cn("space-y-2 rounded-md border p-3", height), children: Array.from({ length: 8 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-full" }, i)) }) : /* @__PURE__ */ jsx(
      DataTable,
      {
        table,
        rowHeight: effectiveRowHeight,
        className: height,
        onRowClick: rowClickHandler,
        getRowId: txRowId,
        activeRowId,
        rowClassName,
        emptyState: /* @__PURE__ */ jsx(EmptyState, { title: emptyTitle, description: emptyDescription })
      }
    ),
    pagination && /* @__PURE__ */ jsx(
      DataTablePagination,
      {
        hasPrev: pagination.hasPrev,
        hasNext: pagination.hasNext,
        onPrev: pagination.onPrev,
        onNext: pagination.onNext,
        label: pagination.label,
        isLoading: isFetching
      }
    ),
    renderRowDetail && /* @__PURE__ */ jsx(
      DrilldownPanel,
      {
        open: detailRow != null,
        onOpenChange: (open) => !open && setDetailRow(null),
        eyebrow: detailEyebrow,
        title: detailRow ? detailTitle?.(detailRow) ?? txRowId(detailRow) ?? "Transaction" : "",
        subtitle: detailRow ? detailSubtitle?.(detailRow) : void 0,
        footer: detailRow ? detailFooter?.(detailRow) : void 0,
        children: detailRow && renderRowDetail(detailRow)
      }
    )
  ] });
}

// src/filters.ts
var TX_FILTER_OPS = {
  /** Membership in a set (multi-select facets: chain_id, finality, type). */
  IN: "in",
  /** Exact match. */
  EQ: "eq",
  /** Case-insensitive substring (hash, address). */
  CONTAINS: "contains",
  /** Numeric/temporal range bounds. */
  GTE: "gte",
  LTE: "lte",
  /** Catalog membership filters. */
  HAS_LABEL: "has_label",
  HAS_TAG: "has_tag",
  HAS_GROUP: "has_group",
  HAS_ENTITY: "has_entity"
};
var CLIENT_FACET_FIELDS = /* @__PURE__ */ new Set(["chain_id", "finality", "type"]);
var CLIENT_CONTAINS_FIELDS = /* @__PURE__ */ new Set(["tx_hash"]);
function asStringArray(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value == null || value === "") return [];
  return [String(value)];
}
function filterRulesToQuery(rules) {
  const query = {};
  const pushAll = (key, values) => {
    if (values.length === 0) return;
    const existing = Array.isArray(query[key]) ? query[key] : [];
    query[key] = [...existing, ...values];
  };
  for (const rule of rules ?? []) {
    switch (rule.op) {
      case TX_FILTER_OPS.IN:
        pushAll(rule.field, asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.EQ:
      case TX_FILTER_OPS.CONTAINS:
        if (rule.value != null && rule.value !== "") query[rule.field] = String(rule.value);
        break;
      case TX_FILTER_OPS.GTE:
        if (rule.value != null) query[`${rule.field}_gte`] = String(rule.value);
        break;
      case TX_FILTER_OPS.LTE:
        if (rule.value != null) query[`${rule.field}_lte`] = String(rule.value);
        break;
      case TX_FILTER_OPS.HAS_LABEL:
        pushAll("label", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_TAG:
        pushAll("tag", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_GROUP:
        pushAll("group", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_ENTITY:
        if (rule.value != null && rule.value !== "") query.entity_id = String(rule.value);
        break;
    }
  }
  return query;
}
function filterRulesToColumnFilters(rules) {
  const filters = [];
  for (const rule of rules ?? []) {
    if (rule.op === TX_FILTER_OPS.IN && CLIENT_FACET_FIELDS.has(rule.field)) {
      const values = asStringArray(rule.value);
      if (values.length) filters.push({ id: rule.field, value: values });
    } else if (rule.op === TX_FILTER_OPS.CONTAINS && CLIENT_CONTAINS_FIELDS.has(rule.field)) {
      if (rule.value != null && rule.value !== "") filters.push({ id: rule.field, value: String(rule.value) });
    }
  }
  return filters;
}
function columnFiltersToFilterRules(filters) {
  const rules = [];
  for (const f of filters) {
    if (Array.isArray(f.value)) {
      const values = f.value.map(String).filter(Boolean);
      if (values.length) rules.push({ field: f.id, op: TX_FILTER_OPS.IN, value: values });
    } else if (typeof f.value === "string" && f.value !== "") {
      rules.push({ field: f.id, op: TX_FILTER_OPS.CONTAINS, value: f.value });
    }
  }
  return rules;
}

// src/useTransactionViews.ts
var TRANSACTIONS_TABLE_ID = "transactions";
function visibilityToColumnPrefs(visibility, columnIds) {
  return columnIds.map((id) => ({ id, visible: visibility[id] !== false }));
}
function columnPrefsToVisibility(prefs) {
  const visibility = {};
  for (const pref of prefs ?? []) visibility[pref.id] = pref.visible !== false;
  return visibility;
}
function sortingToSortRules(sorting) {
  return sorting.map((s) => ({ field: s.id, direction: s.desc ? "desc" : "asc" }));
}
function sortRulesToSorting(rules) {
  return (rules ?? []).map((r) => ({ id: r.field, desc: r.direction === "desc" }));
}
function useTransactionViews(options) {
  const { source, identity, columnIds, autoLoad, defaultDensity = "comfortable" } = options;
  const [columnVisibility, setColumnVisibility] = React12.useState({});
  const [columnFilters, setColumnFilters] = React12.useState([]);
  const [sorting, setSorting] = React12.useState([]);
  const [density, setDensity] = React12.useState(defaultDensity);
  const views = useSavedViews({
    source,
    tableId: TRANSACTIONS_TABLE_ID,
    identity,
    autoLoad,
    onApply: (state) => {
      setColumnVisibility(columnPrefsToVisibility(state.columns));
      setColumnFilters(filterRulesToColumnFilters(state.filters));
      setSorting(sortRulesToSorting(state.sort));
      if (state.density) setDensity(state.density);
    }
  });
  const onColumnVisibilityChange = React12.useCallback(
    (updater) => setColumnVisibility((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const onColumnFiltersChange = React12.useCallback(
    (updater) => setColumnFilters((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const onSortingChange = React12.useCallback(
    (updater) => setSorting((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const captureState = React12.useCallback(
    () => ({
      columns: visibilityToColumnPrefs(columnVisibility, columnIds),
      filters: columnFiltersToFilterRules(columnFilters),
      sort: sortingToSortRules(sorting),
      density
    }),
    [columnVisibility, columnIds, columnFilters, sorting, density]
  );
  const saveCurrent = React12.useCallback(
    (name, scope) => views.save(name, captureState(), scope),
    [views, captureState]
  );
  const serverFilters = React12.useMemo(() => columnFiltersToFilterRules(columnFilters), [columnFilters]);
  return {
    views,
    columnVisibility,
    onColumnVisibilityChange,
    columnFilters,
    onColumnFiltersChange,
    sorting,
    onSortingChange,
    density,
    setDensity,
    serverFilters,
    captureState,
    saveCurrent
  };
}
var TX_COLUMNS = DEFAULT_TX_COLUMNS;
function TransactionExportMenu({
  rows,
  columns,
  formats,
  filenameBase = "transactions",
  label,
  disabled,
  onError
}) {
  return /* @__PURE__ */ jsx(
    ExportMenu,
    {
      rows,
      columns: columns ?? TX_COLUMNS,
      formats,
      filenameBase,
      label,
      disabled,
      onError
    }
  );
}
function channelKey(channel) {
  return typeof channel === "string" ? channel : JSON.stringify(channel);
}
function txRowKey(row) {
  const tx = row.tx_hash ?? row["canonical_tx_id"] ?? row["id"];
  if (tx == null) return null;
  const log5 = row["log_index"];
  return log5 == null ? String(tx) : `${String(tx)}:${String(log5)}`;
}
function mergeTransactionRows(streamed, fetched, max = 1e3) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const row of [...streamed, ...fetched]) {
    const key = txRowKey(row);
    if (key) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push(row);
    if (out.length >= max) break;
  }
  return out;
}
function defaultParse(message) {
  const payload = message.payload;
  if (!payload || typeof payload !== "object") return null;
  if (payload.tx_hash == null && payload["canonical_tx_id"] == null) return null;
  return payload;
}
function useTransactionStream(options) {
  const { channel, enabled = true, max = 500, parse } = options;
  const client = useOptionalStreamingClient();
  const [rows, setRows] = React12.useState([]);
  const [last, setLast] = React12.useState(null);
  const parseRef = React12.useRef(parse ?? defaultParse);
  parseRef.current = parse ?? defaultParse;
  const key = Array.isArray(channel) ? channel.map(channelKey).join("|") : channelKey(channel);
  const channels = React12.useMemo(
    () => Array.isArray(channel) ? [...channel] : [channel],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  );
  React12.useEffect(() => {
    if (!enabled || !client) return;
    const offs = channels.map((ch) => {
      const handler = (message) => {
        setLast(message);
        const row = parseRef.current(message);
        if (row) setRows((prev) => mergeTransactionRows([row], prev, max));
      };
      return typeof ch === "string" ? client.subscribe(ch, handler) : client.subscribeChannel(ch, handler);
    });
    return () => {
      for (const off of offs) off();
    };
  }, [client, channels, enabled, max]);
  const clear = React12.useCallback(() => setRows([]), []);
  return { rows, last, clear };
}
function useChainHeads(options) {
  const tips = useChainTips(options);
  return React12.useMemo(
    () => Object.fromEntries(Object.values(tips).map((tip) => [tip.chainId, tip.headBlock])),
    [tips]
  );
}
function withLiveFinality(rows, heads, now) {
  let moved = false;
  const next = rows.map((row) => {
    const chainId = String(row.chain_id ?? "").toLowerCase();
    const finality = resolveFinality({
      rowFinality: row.finality,
      blockNumber: row.block_number ?? null,
      head: heads[chainId] ?? null,
      blockTimestamp: row.block_timestamp ?? null,
      now,
      chainOrId: chainId
    });
    if (finality === row.finality) return row;
    moved = true;
    return { ...row, finality };
  });
  return moved ? next : rows;
}
function useLiveFinalityRows(rows, heads) {
  const now = useNow();
  return React12.useMemo(() => withLiveFinality(rows, heads, now), [rows, heads, now]);
}
var log = createLogger("tx-table:query");
function useTransactionsQuery(options = {}) {
  const { resource = "transactions", pageSize = 50, query, enabled = true, notifyOnError = false } = options;
  const serializedQuery = JSON.stringify(query ?? {});
  const [cursorStack, setCursorStack] = React12.useState([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  React12.useEffect(() => {
    setCursorStack([]);
  }, [serializedQuery, resource, pageSize]);
  const result = useQuery(
    (client, signal) => client.get(`/${resource}`, {
      query: { ...query ?? {}, limit: pageSize, cursor: cursor ?? void 0 },
      signal
    }),
    {
      enabled,
      deps: [cursor, serializedQuery, resource, pageSize],
      onError: (envelope) => {
        log.error("transactions fetch failed", {
          resource,
          status: envelope.statusCode,
          code: envelope.error.error_code,
          correlationId: envelope.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope);
      }
    }
  );
  const payload = result.data;
  const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : [];
  const pageInfo = (Array.isArray(payload) ? void 0 : payload?.pagination) ?? result.envelope?.pagination;
  const nextCursor = pageInfo?.next ?? null;
  const hasNext = Boolean(nextCursor);
  const hasPrev = cursorStack.length > 0;
  const goNext = React12.useCallback(() => {
    if (nextCursor) setCursorStack((stack) => [...stack, nextCursor]);
  }, [nextCursor]);
  const goPrev = React12.useCallback(() => {
    setCursorStack((stack) => stack.slice(0, -1));
  }, []);
  const reset = React12.useCallback(() => setCursorStack([]), []);
  return {
    rows,
    total: pageInfo?.total,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    hasNext,
    hasPrev,
    goNext,
    goPrev,
    reset,
    refetch: result.refetch,
    pagination: { hasNext, hasPrev, onNext: goNext, onPrev: goPrev }
  };
}
var log2 = createLogger("tx-table:detail");
var POLL_INTERVAL_MS = 2500;
var MAX_POLL_ATTEMPTS = 48;
function useTransactionDetail(canonicalTxId, options = {}) {
  const { resource = "transactions", includeBody = false, enabled = true, notifyOnError = false } = options;
  const [activeScope, setActiveScope] = React12.useState(null);
  const [pollTarget, setPollTarget] = React12.useState(null);
  const [enrichmentTimedOut, setEnrichmentTimedOut] = React12.useState(false);
  const [retryNonce, setRetryNonce] = React12.useState(0);
  const attemptsRef = React12.useRef(0);
  const overridesKey = JSON.stringify(activeScope?.overrides ?? null);
  React12.useEffect(() => {
    setActiveScope(null);
  }, [canonicalTxId]);
  React12.useEffect(() => {
    setPollTarget(null);
    setEnrichmentTimedOut(false);
    attemptsRef.current = 0;
  }, [canonicalTxId, overridesKey]);
  const result = useQuery(
    (client, signal) => {
      const readId = pollTarget ?? canonicalTxId ?? "";
      const query = scopeReadQuery(activeScope, {
        includeBody,
        enqueue: pollTarget ? false : void 0
      });
      return client.get(`/${resource}/${encodeURIComponent(readId)}`, {
        query,
        signal,
        // Opt out of the client's default 30s GET cache. This read backs the
        // surfaces that WRITE to the transaction — the inspector's attribution
        // chips, its notes and status — and each of them refetches on success to
        // show the result. A cached body turns that refetch into a no-op, so an
        // attached label reappears nowhere until the entry ages out, which reads
        // as "the write didn't take". The enrichment poll below has the same
        // problem in slower motion: successive polls share a key, so it would
        // poll the cache rather than the row.
        //
        // The entry can't simply be invalidated after a write either: the read
        // may be keyed by `chain:hash` while the writer only knows the canonical
        // id, and an in-flight GET that started pre-write will repopulate the
        // cache after any invalidation. Not caching a single-record read that is
        // already deduped by `useQuery` is the cheaper correctness.
        cacheTtlMs: 0
      });
    },
    {
      enabled: enabled && Boolean(canonicalTxId),
      deps: [canonicalTxId, overridesKey, includeBody, resource, pollTarget, retryNonce],
      onError: (envelope) => {
        log2.error("transaction detail fetch failed", {
          resource,
          status: envelope.statusCode,
          code: envelope.error.error_code,
          correlationId: envelope.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope);
      }
    }
  );
  const raw = result.data;
  const detail = React12.useMemo(() => {
    if (raw == null) return void 0;
    const parsed = TransactionDetailSchema.safeParse(raw);
    if (parsed.success) return parsed.data;
    log2.warn("transaction detail shape mismatch; using raw payload", {
      issues: parsed.error.issues.length
    });
    return raw;
  }, [raw]);
  const enrichmentStatus = detail?.enrichment_status ?? null;
  const isEnriching = enrichmentStatus === "processing";
  const enrichmentFailed = enrichmentStatus === "failed";
  React12.useEffect(() => {
    if (!isEnriching) return;
    const target = detail?.chain_id && detail?.tx_hash ? `${detail.chain_id}:${detail.tx_hash}` : canonicalTxId ?? null;
    setPollTarget((prev) => prev === target ? prev : target);
  }, [isEnriching, detail?.chain_id, detail?.tx_hash, canonicalTxId]);
  React12.useEffect(() => {
    if (!isEnriching || !pollTarget) return;
    if (result.isFetching) return;
    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
      setEnrichmentTimedOut(true);
      return;
    }
    const timer = window.setTimeout(() => {
      attemptsRef.current += 1;
      result.refetch();
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [isEnriching, pollTarget, result.isFetching, result.refetch]);
  const retryEnrichment = React12.useCallback(() => {
    attemptsRef.current = 0;
    setEnrichmentTimedOut(false);
    setPollTarget(null);
    setRetryNonce((n) => n + 1);
  }, []);
  return {
    detail,
    otherScopes: detail?.other_scopes ?? [],
    activeScope,
    selectScope: setActiveScope,
    enrichmentStatus,
    isEnriching,
    enrichmentFailed,
    enrichmentTimedOut,
    retryEnrichment,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch
  };
}
function toTokenFlow(tx, options) {
  return {
    flows: toTokenFlowRows(tx.token_flows ?? [], options),
    net: toNetFlowRows(tx.net_flows ?? [], options)
  };
}

// src/useGroupView.ts
var log3 = createLogger("tx-table:group");
function useGroupView(groupId, options = {}) {
  const { resource = "groups", includeScene = true, limit, enabled = true, notifyOnError = false } = options;
  const result = useQuery(
    (client, signal) => client.get(`/${resource}/${encodeURIComponent(groupId ?? "")}`, {
      query: {
        // Only send include_scene when turning it OFF — the backend defaults on.
        include_scene: includeScene ? void 0 : "false",
        limit: limit ?? void 0
      },
      signal
    }),
    {
      enabled: enabled && Boolean(groupId),
      deps: [groupId, includeScene, limit, resource],
      onError: (envelope) => {
        log3.error("group view fetch failed", {
          resource,
          status: envelope.statusCode,
          code: envelope.error.error_code,
          correlationId: envelope.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope);
      }
    }
  );
  const payload = result.data;
  const group = React12.useMemo(() => {
    if (payload?.scene == null) return void 0;
    try {
      return parseGroupView(payload);
    } catch (err) {
      log3.warn("group view parse failed; rendering members only", { error: String(err) });
      return void 0;
    }
  }, [payload]);
  const transactions = React12.useMemo(
    () => coerceTransactionRows(payload?.transactions ?? []),
    [payload]
  );
  return {
    group,
    transactions,
    descriptor: payload?.group,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch
  };
}
function channelKey2(channel) {
  return typeof channel === "string" ? channel : JSON.stringify(channel);
}
function matchesGroup(message, groupId) {
  const payload = message.payload;
  if (!payload || typeof payload !== "object") return false;
  if (String(payload.id ?? "") === groupId && groupId !== "") return true;
  const header = payload.header;
  if (header && typeof header === "object" && String(header.group_id ?? "") === groupId && groupId !== "") {
    return true;
  }
  return false;
}
function useGroupViewLive(groupId, options = {}) {
  const { channels, debounceMs = 400, ...viewOptions } = options;
  const base = useGroupView(groupId, viewOptions);
  const client = useOptionalStreamingClient();
  const [lastEventAt, setLastEventAt] = React12.useState(null);
  const [eventCount, setEventCount] = React12.useState(0);
  const refetchRef = React12.useRef(base.refetch);
  refetchRef.current = base.refetch;
  const timer = React12.useRef(null);
  const list = React12.useMemo(
    () => channels == null ? [] : Array.isArray(channels) ? [...channels] : [channels],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      channels == null ? "" : Array.isArray(channels) ? channels.map(channelKey2).join("|") : channelKey2(channels)
    ]
  );
  const enabled = viewOptions.enabled ?? true;
  const isLive = Boolean(client) && list.length > 0 && enabled && Boolean(groupId);
  React12.useEffect(() => {
    if (!isLive || !client || !groupId) return;
    const onMessage = (message) => {
      if (!matchesGroup(message, groupId)) return;
      setLastEventAt(Date.now());
      setEventCount((n) => n + 1);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => refetchRef.current(), debounceMs);
    };
    const offs = list.map(
      (ch) => typeof ch === "string" ? client.subscribe(ch, onMessage) : client.subscribeChannel(ch, onMessage)
    );
    return () => {
      for (const off of offs) off();
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [client, list, isLive, groupId, debounceMs]);
  return { ...base, isLive, lastEventAt, eventCount };
}
var OWN_DESCRIPTION = "The copy resolved for you (your private row, else the tenant-shared one).";
function buildScopeChoices(otherScopes, activeScope, ownLabel = "Your view") {
  return buildScopeChoices$1(otherScopes, activeScope, {
    ownLabel,
    ownDescription: OWN_DESCRIPTION,
    scopeLabel: transactionScopeLabel,
    scopeDescription: transactionScopeDescription
  });
}
function TransactionScopeSwitcher({
  otherScopes,
  activeScope = null,
  onSelect,
  busy = false,
  className
}) {
  return /* @__PURE__ */ jsx(
    ScopeSwitcher,
    {
      otherScopes,
      activeScope,
      onSelect,
      busy,
      className,
      copy: {
        ownLabel: "Your view",
        ownDescription: OWN_DESCRIPTION,
        scopeLabel: transactionScopeLabel,
        scopeDescription: transactionScopeDescription,
        ariaLabel: "View this transaction at another scope"
      }
    }
  );
}
function TransactionDetailPanel({
  canonicalTxId,
  children,
  onScopeChange,
  className,
  ...queryOptions
}) {
  const { detail, otherScopes, activeScope, selectScope, isLoading, isFetching, error, refetch } = useTransactionDetail(canonicalTxId, queryOptions);
  const handleSelect = React12.useCallback(
    (scope) => {
      selectScope(scope);
      onScopeChange?.(scope);
    },
    [selectScope, onScopeChange]
  );
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex items-center justify-center py-10 text-muted-foreground", className), children: /* @__PURE__ */ jsx(Spinner, {}) });
  }
  if (error) {
    return /* @__PURE__ */ jsx(ErrorState, { error, onRetry: refetch, className, size: "sm" });
  }
  if (!detail) return /* @__PURE__ */ jsx("div", { className: cn("py-10 text-center text-sm text-muted-foreground", className) });
  const viewingLabel = activeScope ? transactionScopeLabel(activeScope.scope) : "Your view";
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "Viewing" }),
      /* @__PURE__ */ jsx(Badge, { variant: activeScope ? "outline" : "secondary", children: viewingLabel }),
      detail.saved && !activeScope ? /* @__PURE__ */ jsx(Badge, { variant: "outline", title: "This copy is saved to your workspace.", children: "Saved" }) : null,
      isFetching ? /* @__PURE__ */ jsx(Spinner, { className: "h-3.5 w-3.5 text-muted-foreground" }) : null
    ] }),
    /* @__PURE__ */ jsx(
      TransactionScopeSwitcher,
      {
        otherScopes,
        activeScope,
        onSelect: handleSelect,
        busy: isFetching
      }
    ),
    children ? children(detail) : null
  ] });
}
var log4 = createLogger("tx-table:hydration");
var TX_LARGE_ATTRIBUTES = [
  "input",
  "output",
  "data",
  "logs",
  "internal_transactions",
  "native_transfers",
  "erc20_transfers",
  "nft_transfers",
  "decoded_transaction",
  "decoded_call",
  // The protocol-shaped semantic tree (`CanonicalTransaction.semantic`) the
  // `@authe/tx-semantic` view renders. Offloaded like the other heavy forensic
  // attributes and hydrated on drill-down.
  "semantic"
];
function createCdnOffloadFetcher(config) {
  return async (placeholder) => {
    const url = cdnUrlForKey(placeholder.key, config);
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit"
    });
    if (!res.ok) throw new Error(`CDN offload fetch failed (${res.status}) for ${placeholder.attribute}`);
    return res.json();
  };
}
function hasOffloadedAttributes(row, attributes = TX_LARGE_ATTRIBUTES) {
  return attributes.some((attr) => isOffloadPlaceholder(row[attr]));
}
async function hydrateTxRow(row, fetcher, attributes = TX_LARGE_ATTRIBUTES) {
  if (!hasOffloadedAttributes(row, attributes)) return row;
  const attrSet = new Set(attributes);
  return rehydrateItem(row, (placeholder) => {
    if (!attrSet.has(placeholder.attribute)) return Promise.resolve(row[placeholder.attribute]);
    return fetcher(placeholder);
  });
}
function useHydratedTxRow(row, options) {
  const { fetcher, attributes = TX_LARGE_ATTRIBUTES, enabled = true } = options;
  const [hydrated, setHydrated] = React12.useState(row);
  const [isLoading, setIsLoading] = React12.useState(false);
  const [error, setError] = React12.useState(null);
  const [trigger, setTrigger] = React12.useState(0);
  React12.useEffect(() => {
    setHydrated(row);
    setError(null);
  }, [row]);
  React12.useEffect(() => {
    if (!enabled && trigger === 0) return;
    if (!hasOffloadedAttributes(row, attributes)) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    hydrateTxRow(row, fetcher, attributes).then((next) => {
      if (!cancelled) setHydrated(next);
    }).catch((err) => {
      if (cancelled) return;
      log4.warn("row hydration failed", { error: String(err) });
      setError(err);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [row, fetcher, enabled, trigger]);
  const hydrate = React12.useCallback(() => setTrigger((n) => n + 1), []);
  return { row: hydrated, isLoading, error, hydrate };
}
function useLazyAttribute(value, fetcher) {
  const placeholder = isOffloadPlaceholder(value) ? value : null;
  const [resolved, setResolved] = React12.useState(placeholder ? void 0 : value);
  const [isLoading, setIsLoading] = React12.useState(false);
  const [error, setError] = React12.useState(null);
  React12.useEffect(() => {
    setResolved(isOffloadPlaceholder(value) ? void 0 : value);
    setError(null);
  }, [value]);
  const load = React12.useCallback(() => {
    if (!placeholder || resolved !== void 0 || isLoading) return;
    setIsLoading(true);
    setError(null);
    fetcher(placeholder).then((v) => {
      setResolved(v);
    }).catch((err) => {
      log4.warn("attribute hydration failed", { attribute: placeholder.attribute, error: String(err) });
      setError(err);
    }).finally(() => {
      setIsLoading(false);
    });
  }, [placeholder, resolved, isLoading, fetcher]);
  return { value: resolved, isLoading, error, load };
}
function createCachedCdnOffloadFetcher(config, cache) {
  const base = createCdnOffloadFetcher(config);
  return async (placeholder) => {
    const cached = await cache.getStatic(placeholder.key);
    if (cached !== void 0) return cached;
    const value = await base(placeholder);
    await cache.setStatic(placeholder.key, value);
    return value;
  };
}
function txRowEnvelopeHeader(row) {
  return {
    canonical_tx_id: row.canonical_tx_id,
    chain_id: row.chain_id,
    pk: null,
    sk: null,
    block_hash: row.block_hash ?? "",
    block_number: row.block_number ?? 0,
    block_timestamp: row.block_timestamp ?? 0,
    finality: typeof row.finality === "string" ? row.finality : "unknown",
    participant_summary: row.participant_summary ?? {},
    total_value_raw: row.total_value_raw ?? "0",
    counts: row.counts ?? {},
    s3_key: row.s3_key ?? null,
    s3_etag: row.s3_etag ?? null,
    body_bytes: row.body_bytes ?? 0,
    body_compression: row.body_compression ?? "none",
    // The lean list projection carries the manifest (catchall passthrough) so the
    // body loader knows which heavy slices to lazy-load + merge after the core.
    body_slices: normalizeBodySlices(row.body_slices),
    schema_version: row.schema_version ?? "1"
  };
}
function normalizeBodySlices(value) {
  if (!value || typeof value !== "object") return void 0;
  const out = {};
  for (const [slice, ref] of Object.entries(value)) {
    const o = ref && typeof ref === "object" ? ref : {};
    out[slice] = {
      bytes: typeof o.bytes === "number" ? o.bytes : 0,
      etag: typeof o.etag === "string" ? o.etag : null
    };
  }
  return Object.keys(out).length > 0 ? out : void 0;
}
function createTxBodyLoader(config) {
  return createEnvelopeBodyLoader(config);
}
function cloneBody(body) {
  const sc = globalThis.structuredClone;
  if (typeof sc === "function") return sc(body);
  return JSON.parse(JSON.stringify(body));
}
function useEnvelopeBody(row, loader, options = {}) {
  const { enabled = false, mergeSlices = false } = options;
  const [body, setBody] = React12.useState(void 0);
  const [isLoading, setIsLoading] = React12.useState(false);
  const [error, setError] = React12.useState(null);
  const [pendingSlices, setPendingSlices] = React12.useState([]);
  const [trigger, setTrigger] = React12.useState(enabled ? 1 : 0);
  const sliceSelector = Array.isArray(mergeSlices) ? [...mergeSlices].sort().join(",") : String(mergeSlices);
  React12.useEffect(() => {
    setBody(void 0);
    setError(null);
    setPendingSlices([]);
    setTrigger(enabled ? 1 : 0);
  }, [row.canonical_tx_id, row.s3_etag, enabled]);
  React12.useEffect(() => {
    if (trigger === 0) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    const header = txRowEnvelopeHeader(row);
    const wantedSlices = () => {
      const manifest = header.body_slices ?? {};
      const names = Array.isArray(mergeSlices) ? mergeSlices : mergeSlices ? Object.keys(manifest) : [];
      return names.filter((slice) => manifest[slice]);
    };
    void (async () => {
      try {
        const core = await loader.loadBody(header).get();
        if (cancelled) return;
        setBody(core);
        const slices = wantedSlices();
        setPendingSlices(slices);
        if (slices.length === 0) return;
        const working = cloneBody(core);
        await Promise.all(
          slices.map(async (slice) => {
            try {
              const payload = await loader.loadSlice(header, slice);
              if (cancelled || payload === void 0) return;
              mergeSlice(working, slice, payload);
              setBody({ ...working });
            } catch (err) {
              if (!cancelled) {
                log4.warn("envelope slice load failed", {
                  tx: row.canonical_tx_id,
                  slice,
                  error: String(err)
                });
              }
            } finally {
              if (!cancelled) {
                setPendingSlices((prev) => prev.filter((s) => s !== slice));
              }
            }
          })
        );
      } catch (err) {
        if (cancelled) return;
        log4.warn("envelope body load failed", { tx: row.canonical_tx_id, error: String(err) });
        setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row.canonical_tx_id, row.s3_etag, loader, trigger, sliceSelector]);
  const load = React12.useCallback(() => setTrigger((n) => n === 0 ? 1 : n + 1), []);
  return { body, isLoading, error, pendingSlices, load };
}

// src/graphqlFeed.ts
function feedRowToEnrichedTxRow(row) {
  const primaryEntity = row.entities[0];
  const base = coerceTransactionRow({
    canonical_tx_id: row.canonicalTxId,
    chain_id: row.chainId,
    block_timestamp: row.blockTimestamp,
    primary_entity_id: primaryEntity?.id ?? null
  });
  const labels = row.labels.map((l) => ({ label: l.name ?? l.catalogId }));
  const tags = row.tags.map((t) => ({ label: t.name ?? t.catalogId }));
  const groups = row.groups.map((g) => ({ id: g.catalogId, name: g.name }));
  const enrichment = {
    labels,
    tags,
    groups,
    entity: primaryEntity ? { id: primaryEntity.id, name: primaryEntity.name ?? primaryEntity.id } : null
  };
  return { ...base, enrichment };
}
function feedToEnrichedRows(feed) {
  if (!feed) return [];
  const items = Array.isArray(feed) ? feed : feed.items;
  return (items ?? []).map(feedRowToEnrichedTxRow);
}
function str(value) {
  return typeof value === "string" && value ? value : void 0;
}
function enrichFallbackRow(raw) {
  const row = coerceTransactionRow(raw);
  const r = row;
  const to = str(r.to);
  const entityName = str(r.to_entity_name) ?? str(r.to_address_label);
  const enrichment = {
    from: str(r.from),
    to,
    entity: entityName ? { id: to ?? entityName, name: entityName } : null
  };
  return { ...row, enrichment };
}
var EMPTY_ROWS = [];
function restItems(payload) {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.items) ? payload.items : [];
}
function useLatestTransactionsFeed(options = {}) {
  const limit = options.limit ?? 25;
  const active = useTransport("transactions.recentFeed") === "graphql";
  const query = useGraphqlQuery(
    "transactions.recentFeed",
    {
      graphql: async (client, signal) => feedToEnrichedRows(await fetchLatestTransactions(client, { limit }, { signal })),
      rest: async (client, signal) => {
        const payload = unwrapData(
          await client.get("/transactions", { query: { limit }, signal })
        );
        return restItems(payload).map(enrichFallbackRow);
      }
    },
    { enabled: (options.enabled ?? true) && active, deps: [limit] }
  );
  const rows = query.data ?? EMPTY_ROWS;
  return {
    rows,
    isLoading: query.isLoading && active,
    isFetching: query.isFetching,
    error: query.error,
    active,
    refetch: query.refetch
  };
}

export { CLIENT_CONTAINS_FIELDS, CLIENT_FACET_FIELDS, TRANSACTIONS_TABLE_ID, TX_COLUMN_LABELS, TX_FILTER_OPS, TX_LARGE_ATTRIBUTES, TransactionDetailPanel, TransactionExportMenu, TransactionFacets, TransactionLegend, TransactionScopeSwitcher, TransactionTable, buildScopeChoices, columnFiltersToFilterRules, columnPrefsToVisibility, createCachedCdnOffloadFetcher, createCdnOffloadFetcher, createTransactionColumns, createTxBodyLoader, feedRowToEnrichedTxRow, feedToEnrichedRows, filterRulesToColumnFilters, filterRulesToQuery, getRowEnrichment, hasOffloadedAttributes, hydrateTxRow, isSystemTxRow, matchesGroup, mergeTransactionRows, readInitialColumnVisibility, resolveRisk, rowProtocols, sortRulesToSorting, sortingToSortRules, summarizeTransactions, toTokenFlow, txRowEnvelopeHeader, txRowId, txRowKey, useChainHeads, useEnvelopeBody, useGroupView, useGroupViewLive, useHydratedTxRow, useLatestTransactionsFeed, useLazyAttribute, useLiveFinalityRows, usePersistColumnVisibility, useTransactionDetail, useTransactionStream, useTransactionViews, useTransactionsQuery, visibilityToColumnPrefs, withLiveFinality };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map